import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, loading, isAuthenticated, sessionChecked } = useAuth();
  const location = useLocation();

  // Force re-verification of auth state on every render/navigation
  useEffect(() => {
    if (sessionChecked && !loading && !isAuthenticated) {
      console.log('Auth verification failed on navigation, redirecting to login');
      // Store the attempted URL for redirection after login
      sessionStorage.setItem('redirectTo', location.pathname);
    }
  }, [loading, isAuthenticated, sessionChecked, location.pathname]);

  // Check if user is already authenticated - this will speed up rendering
  if (isAuthenticated && user) {
    return <>{children}</>;
  }

  // Show loading indicator while auth state is being determined
  if (loading || (!sessionChecked && !isAuthenticated)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-xl mb-4">Verifying authentication...</div>
        <div className="w-12 h-12 border-4 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Store the attempted URL for redirection after login
    sessionStorage.setItem('redirectTo', location.pathname);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // For admin routes, we would need to check the user's role
  // This would require fetching the team_members data to check role
  if (requireAdmin) {
    // This is a placeholder for actual admin check
    const isAdmin = true; // Replace with actual admin check
    
    if (!isAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
} 